import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { QCRecordsListResult } from "@/modules/qc/domain/types/record";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";

type QCRecordRow = QCRecordsListResult["data"][number];

type QCRecordTableProps = {
  data: QCRecordRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit?: (record: QCRecordRow) => void;
  onView?: (id: string) => void;
  onDelete?: (record: QCRecordRow) => void;
};

const statusLabels: Record<string, string> = {
  pass: "ຜ່ານ",
  fail: "ບໍ່ຜ່ານ",
  pending: "ກຳລັງກວດສອບ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  pass: "default",
  fail: "destructive",
  pending: "secondary",
};

export function QCRecordTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
  onDelete,
}: QCRecordTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<QCRecordRow>[] = [
    {
      id: "qcStandardName",
      header: "ມາດຕະຖານ QC",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.qcStandard.name}</span>
      ),
      size: 200,
    },
    {
      id: "inspectorName",
      header: "ຜູ້ກວດສອບ",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.inspector?.name?.trim()
            ? row.original.inspector.name
            : "—"}
        </span>
      ),
      size: 150,
    },
    createSortableColumn<QCRecordRow>("inspectedAt", "ວັນທີກວດສອບ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTimeLocal(row.original.inspectedAt)}
        </span>
      ),
    }),
    {
      id: "location",
      header: "ສະຖານທີ່",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.location || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "workItemType",
      header: "ປະເພດງານ",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.workItemType?.name || "-"}
        </span>
      ),
      size: 150,
    },
    {
      id: "workItemId",
      header: "ລະຫັດງານ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.workItemId || "-"}</span>
      ),
      size: 120,
    },
    {
      id: "overallStatus",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge
          variant={statusColors[row.original.overallStatus] || "secondary"}
        >
          {statusLabels[row.original.overallStatus] ||
            row.original.overallStatus}
        </Badge>
      ),
      size: 120,
    },
    createSortableColumn<QCRecordRow>("createdAt", "ວັນທີສ້າງ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: QCRecordRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has(Permissions.qc_record.read) && onView) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id),
          });
        }

        if (has(Permissions.qc_record.update) && onEdit) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }

        if (has(Permissions.qc_record.delete) && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const record = row.original;
              const ok = await confirm({
                title: "ລຶບບັນທຶກ QC",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບບັນທຶກ QC ນີ້? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
                actionText: "ລຶບ",
                cancelText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(record);
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="record-table">
      <DataTable<QCRecordRow, unknown>
        noDataMessage="ບໍ່ພົບບັນທຶກ QC"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
